import type { Tags } from "./osm";

/** Encoded `highway` classes (see docs/ARCHITECTURE.md). */
export const enum Hw {
  Other = 0,
  Footway = 1,
  Path = 2,
  Track = 3,
  Pedestrian = 4,
  Steps = 5,
  Cycleway = 6,
  LivingStreet = 7,
  Residential = 8,
  Service = 9,
  Unclassified = 10,
  Tertiary = 11,
  Secondary = 12,
  Primary = 13,
  Bridleway = 14,
}

export const enum Surface {
  Unknown = 0,
  Paved = 1,
  Compacted = 2,
  Unpaved = 3,
}

export const enum Flag {
  Lit = 1,
  Sidewalk = 2,
  Tunnel = 4,
  Bridge = 8,
}

const HIGHWAY: Readonly<Record<string, Hw>> = {
  footway: Hw.Footway,
  path: Hw.Path,
  track: Hw.Track,
  pedestrian: Hw.Pedestrian,
  steps: Hw.Steps,
  cycleway: Hw.Cycleway,
  living_street: Hw.LivingStreet,
  residential: Hw.Residential,
  service: Hw.Service,
  unclassified: Hw.Unclassified,
  road: Hw.Unclassified,
  tertiary: Hw.Tertiary,
  tertiary_link: Hw.Tertiary,
  secondary: Hw.Secondary,
  secondary_link: Hw.Secondary,
  primary: Hw.Primary,
  primary_link: Hw.Primary,
  bridleway: Hw.Bridleway,
};

/** Highways a runner may only use when explicitly allowed or with a sidewalk. */
const ONLY_IF_ALLOWED = new Set(["trunk", "trunk_link"]);

const FOOT_YES = new Set(["yes", "designated", "permissive"]);
const FOOT_NO = new Set(["no", "use_sidepath"]);
const ACCESS_NO = new Set(["no", "private"]);
const SERVICE_NO = new Set(["parking_aisle", "drive-through"]);

const PAVED = new Set([
  "paved",
  "asphalt",
  "concrete",
  "concrete:plates",
  "concrete:lanes",
  "paving_stones",
  "sett",
  "cobblestone",
  "unhewn_cobblestone",
  "metal",
  "wood",
  "rubber",
  "tartan",
]);
const COMPACTED = new Set(["compacted", "fine_gravel"]);
const UNPAVED = new Set([
  "unpaved",
  "gravel",
  "dirt",
  "earth",
  "ground",
  "grass",
  "mud",
  "sand",
  "pebblestone",
  "woodchips",
  "rock",
  "grass_paver",
]);

export function hasSidewalk(t: Tags): boolean {
  const s = t["sidewalk"] ?? t["sidewalk:both"];
  return (
    (s !== undefined && s !== "no" && s !== "none" && s !== "separate") ||
    t["sidewalk:left"] === "yes" ||
    t["sidewalk:right"] === "yes"
  );
}

/** Can a runner use this way? */
export function isRunnable(t: Tags | undefined): boolean {
  if (!t) return false;
  const hw = t["highway"];
  if (hw === undefined) return false;
  const foot = t["foot"];
  if (foot !== undefined && FOOT_NO.has(foot)) return false;
  const footYes = foot !== undefined && FOOT_YES.has(foot);
  const access = t["access"];
  if (access !== undefined && ACCESS_NO.has(access) && !footYes) return false;
  if (t["indoor"] === "yes") return false;
  if (ONLY_IF_ALLOWED.has(hw)) return footYes || hasSidewalk(t);
  if (!(hw in HIGHWAY)) return false;
  if (hw === "service" && t["service"] !== undefined && SERVICE_NO.has(t["service"])) return false;
  return true;
}

export function encodeHighway(t: Tags): Hw {
  const hw = t["highway"] ?? "";
  if (ONLY_IF_ALLOWED.has(hw)) return Hw.Primary;
  return HIGHWAY[hw] ?? Hw.Other;
}

export function encodeSurface(t: Tags): Surface {
  const s = t["surface"];
  if (s !== undefined) {
    if (PAVED.has(s)) return Surface.Paved;
    if (COMPACTED.has(s)) return Surface.Compacted;
    if (UNPAVED.has(s)) return Surface.Unpaved;
    return Surface.Unknown;
  }
  // Sensible defaults when untagged.
  const hw = encodeHighway(t);
  if (hw === Hw.Track || hw === Hw.Path || hw === Hw.Bridleway) {
    const grade = t["tracktype"];
    return grade === "grade1" ? Surface.Paved : Surface.Unpaved;
  }
  if (hw >= Hw.LivingStreet || hw === Hw.Cycleway || hw === Hw.Pedestrian) return Surface.Paved;
  return Surface.Unknown;
}

export function encodeFlags(t: Tags): number {
  let f = 0;
  const lit = t["lit"];
  if (lit !== undefined && lit !== "no") f |= Flag.Lit;
  if (hasSidewalk(t)) f |= Flag.Sidewalk;
  if (t["tunnel"] !== undefined && t["tunnel"] !== "no") f |= Flag.Tunnel;
  if (t["bridge"] !== undefined && t["bridge"] !== "no") f |= Flag.Bridge;
  return f;
}

const GREEN_AREA: Readonly<Record<string, ReadonlySet<string>>> = {
  leisure: new Set(["park", "nature_reserve", "garden", "golf_course"]),
  landuse: new Set(["forest", "grass", "meadow", "recreation_ground", "village_green"]),
  natural: new Set(["wood", "scrub", "heath", "grassland", "beach", "water"]),
  waterway: new Set(["riverbank"]),
};

/** Polygons that make a nearby edge "green" (parks, forest, water, shores). */
export function isGreenArea(t: Tags | undefined): boolean {
  if (!t) return false;
  for (const [k, vals] of Object.entries(GREEN_AREA)) {
    const v = t[k];
    if (v !== undefined && vals.has(v)) return true;
  }
  return false;
}
